/**
 * Safe DOM utilities to prevent iOS Chrome NotFoundError
 * These utilities provide fallbacks for DOM operations that can fail on iOS Chrome
 * due to timing issues and browser-specific behaviors.
 */

/**
 * Safely get bounding client rect from an element
 * @param {Element} element - The DOM element
 * @returns {DOMRect|null} - The bounding client rect or null if failed
 */
export const safeGetBoundingClientRect = (element) => {
  try {
    if (!element || typeof element.getBoundingClientRect !== 'function') {
      return null
    }
    return element.getBoundingClientRect()
  } catch (error) {
    console.warn('DOM access error in getBoundingClientRect:', error)
    return null
  }
}

/**
 * Safely scroll element into view
 * @param {Element} element - The DOM element to scroll into view
 * @param {ScrollIntoViewOptions} options - Scroll options
 * @returns {boolean} - True if successful, false if failed
 */
export const safeScrollIntoView = (element, options = { behavior: 'smooth', block: 'start' }) => {
  try {
    if (!element || typeof element.scrollIntoView !== 'function') {
      return false
    }
    element.scrollIntoView(options)
    return true
  } catch (error) {
    console.warn('DOM access error in scrollIntoView:', error)
    return false
  }
}

/**
 * Safely access window properties
 * @param {string} property - The window property to access
 * @param {any} fallback - Fallback value if access fails
 * @returns {any} - The property value or fallback
 */
export const safeWindowAccess = (property, fallback = null) => {
  try {
    if (typeof window === 'undefined' || !(property in window)) {
      return fallback
    }
    return window[property]
  } catch (error) {
    console.warn(`DOM access error accessing window.${property}:`, error)
    return fallback
  }
}

/**
 * Safely access element properties
 * @param {Element} element - The DOM element
 * @param {string} property - The property to access
 * @param {any} fallback - Fallback value if access fails
 * @returns {any} - The property value or fallback
 */
export const safeElementAccess = (element, property, fallback = null) => {
  try {
    if (!element || !(property in element)) {
      return fallback
    }
    return element[property]
  } catch (error) {
    console.warn(`DOM access error accessing element.${property}:`, error)
    return fallback
  }
}
